import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { cn, formatCount } from '@wit-support/ui';
import { formatMinor, formatMinorByCurrency } from '../format.js';
/*
 * Деньги «итог в день» — только из ответа сервера (спека «Конструктор FB v4»
 * §3.11). Все суммы — общим `formatMinor` (локаль валюты, USD = «$962.41»);
 * формула собирается из СТРУКТУРНЫХ полей сервера (`formula`, `breakdown`,
 * `centsMinor`, `totalBeforeCentsMinor`) — клиент ничего не умножает и не
 * складывает; готовая строка `formulaRu` — только запасной текст.
 */
/** Итог в день по строке сервера: «$962.41». Валюта — кабинета (из строки). */
export function spendTotalText(line) {
    // итог не годится (валюта не в центах, пустой бюджет — ревью SB-1): суммы нет, только причина
    // сервер отдаёт `totalMinor: null` на непригодной строке — суммы не показываем никогда
    if (line.usable === false || line.totalMinor == null) {
        return line.formulaRu?.trim() || 'Итога в день нет';
    }
    return formatMinor(line.totalMinor, line.currency);
}
/**
 * Строки денег: `spend.lines` сервера, а у старого auto-ads без `spend` —
 * `totals.dailyBudgetMinorByCurrency` (итог по валюте без формулы). Без
 * арифметики — только перекладка.
 */
export function spendLinesOrTotals(spend, totals) {
    if (spend?.lines && spend.lines.length > 0)
        return spend.lines;
    // сервер сказал «итог не годится» — запасной итог из `totals` его не заменяет (ревью SB-1)
    if (spend?.usable === false)
        return [];
    return Object.entries(totals ?? {}).map(([currency, totalMinor]) => ({ currency, totalMinor }));
}
/**
 * Итог пачки неполный (ревью SB-1): сервер сказал `usable: false` или у части
 * запусков деньги неизвестны (`unknownLaunches > 0`) — уверенной суммы нет,
 * «больше обычного» не показывается.
 */
export function spendIncomplete(spend) {
    return spend?.usable === false || (spend?.unknownLaunches ?? 0) > 0 || spend?.notBuilt != null;
}
/**
 * Итог в день для текста (подтверждения, шапка плана) — ЕДИНОЕ правило
 * (ревью SB-4 P1-2): итог неполный (`spendIncomplete`) — «итог неполный»
 * вместо заниженной суммы `totals`; иначе — `totals` по валютам.
 */
export function dayTotalText(spend, totals) {
    return spendIncomplete(spend) ? 'итог неполный' : formatMinorByCurrency(totals);
}
/** «Сумма неизвестна для 2 запусков» — когда у части запусков деньги неизвестны. */
export function unknownLaunchesText(n) {
    if (n == null || n <= 0)
        return null;
    return `Сумма неизвестна для ${formatCount(n, ['запуска', 'запусков', 'запусков'])}`;
}
/** Строки «почему итога нет» неполного итога: причины негодных строк и неизвестные запуски. */
export function incompleteReasons(spend) {
    const out = (spend?.lines ?? [])
        .filter((l) => l.usable === false || l.totalMinor == null)
        .map((l) => l.formulaRu?.trim() || 'Итога в день нет');
    const notBuilt = spend?.notBuilt?.messageRu?.trim();
    if (notBuilt)
        out.push(notBuilt);
    const unknown = unknownLaunchesText(spend?.unknownLaunches);
    if (unknown)
        out.push(unknown);
    return out.length > 0 ? out : ['Итога в день нет'];
}
/**
 * Сумма для подписи кнопки: «$783.12/день» или «$10.00/день + €5.00/день»
 * (по части на валюту — готовые суммы сервера).
 */
export function spendButtonAmount(lines) {
    if (lines.length === 0 || lines.some((l) => l.usable === false || l.totalMinor == null))
        return null;
    return lines.map((l) => `${spendTotalText(l)}/день`).join(' + ');
}
/**
 * Формула строки для показа: массив строк (1 — одной строкой, > 1 — столбиком).
 * - `formula` (форма 1): «$80.00 × 12 групп объявлений = $960.00» или
 *   «… + уникальные центы $2.41 = $962.41»; множитель 1 опускается;
 * - 2–4 строки `breakdown` (форма 2): «$80.00 × 6 = $480.00», …,
 *   «уникальные центы +$1.46», «всего $781.46» — столбиком;
 * - иначе — запасной текст сервера `formulaRu` одной строкой (форма 3, старый
 *   ответ); нет и его — без формулы.
 */
export function formulaRows(line) {
    // у негодного итога формулы нет: причина уже в `spendTotalText`
    if (line.usable === false || line.totalMinor == null)
        return [];
    const total = line.totalMinor;
    const m = (minor) => formatMinor(minor, line.currency);
    const cents = line.centsMinor ?? 0;
    const f = line.formula;
    if (f) {
        const unit = f.count === 1 ? m(f.unitMinor) : `${m(f.unitMinor)} × ${f.count} ${f.unitLabel}`;
        if (cents > 0)
            return [`${unit} + уникальные центы ${m(cents)} = ${m(total)}`];
        if (f.count === 1)
            return [];
        const before = line.totalBeforeCentsMinor ?? total;
        return [`${unit} = ${m(before)}`];
    }
    const rows = line.breakdown ?? [];
    if (rows.length >= 2 && rows.length <= 4) {
        return [
            // один бюджет такого размера — без «× 1 = …» (как в `formulaRu` сервера)
            ...rows.map((r) => r.count === 1
                ? m(r.baseUnitMinor)
                : `${m(r.baseUnitMinor)} × ${r.count} = ${m(r.subtotalMinor)}`),
            ...(cents > 0 ? [`уникальные центы +${m(cents)}`] : []),
            `всего ${m(total)}`,
        ];
    }
    const text = line.formulaRu?.trim();
    return text ? [text] : [];
}
/** Формула строки: одна строка — абзацем, несколько — списком столбиком. */
export function SpendFormula({ line, className }) {
    const rows = formulaRows(line);
    if (rows.length === 0)
        return null;
    const cls = cn('text-sm tabular-nums text-[var(--color-text-primary)]', className);
    if (rows.length === 1)
        return _jsx("p", { className: cls, children: rows[0] });
    return (_jsx("ul", { className: cn('flex flex-col', cls), children: rows.map((row, i) => (_jsx("li", { children: row }, i))) }));
}
/**
 * «В день всего: $962.41» крупно + формула под ней (спека v4 §11.0).
 * Несколько валют — по строке на валюту. `aria-live="polite"` — читалка
 * объявляет новую сумму при смене копий/галочек.
 */
export function SpendSummary({ spend, state = 'ready', fallbackByCurrency, className, }) {
    const lines = spendLinesOrTotals(spend, fallbackByCurrency);
    const pending = state === 'pending';
    // итог неполный (ревью SB-1): без уверенной суммы — «итога нет» и почему
    if (state !== 'unavailable' && spendIncomplete(spend)) {
        return (_jsxs("div", { className: cn('flex min-w-0 flex-col gap-1 [overflow-wrap:anywhere]', className), "aria-live": "polite", "aria-atomic": "true", "aria-busy": pending || undefined, "data-testid": "spend-summary", children: [_jsx("p", { className: "text-base font-semibold text-[var(--color-text-primary)]", children: "\u0412 \u0434\u0435\u043D\u044C \u0432\u0441\u0435\u0433\u043E: \u0438\u0442\u043E\u0433 \u043D\u0435\u043F\u043E\u043B\u043D\u044B\u0439" }), incompleteReasons(spend).map((r) => (_jsx("p", { className: "text-sm text-[var(--color-text-secondary)]", "data-testid": "spend-incomplete", children: r }, r))), pending ? (_jsx("p", { className: "text-xs text-[var(--color-text-secondary)]", children: "\u043F\u0435\u0440\u0435\u0441\u0447\u0438\u0442\u044B\u0432\u0430\u0435\u043C\u2026" })) : null] }));
    }
    // недоступен — прежнюю сумму не показываем и формулу не собираем сами
    const shown = state === 'unavailable' ? [] : lines;
    return (_jsx("div", { className: cn('flex min-w-0 flex-col gap-1 [overflow-wrap:anywhere]', className), "aria-live": "polite", "aria-atomic": "true", "aria-busy": pending || undefined, "data-testid": "spend-summary", children: shown.length === 0 ? (_jsx("p", { className: "text-sm text-[var(--color-text-secondary)]", children: pending ? 'Пересчитываем…' : 'Итог — после «Проверить»' })) : (_jsxs(_Fragment, { children: [shown.map((line) => (_jsxs("div", { className: cn('flex flex-col gap-0.5', pending && 'opacity-60'), children: [_jsxs("p", { className: "text-xl font-semibold text-[var(--color-text-primary)] sm:text-2xl", children: ["\u0412 \u0434\u0435\u043D\u044C \u0432\u0441\u0435\u0433\u043E: ", _jsx("span", { className: "tabular-nums", children: spendTotalText(line) })] }), _jsx(SpendFormula, { line: line })] }, line.currency))), pending ? (_jsx("p", { className: "text-xs text-[var(--color-text-secondary)]", children: "\u043F\u0435\u0440\u0435\u0441\u0447\u0438\u0442\u044B\u0432\u0430\u0435\u043C\u2026" })) : null] })) }));
}
//# sourceMappingURL=spend-summary.js.map